import { useEffect, useRef, useState } from "react";
import { Check, ClipboardCopy, Send } from "lucide-react";
import { toast } from "sonner";

import type { ManualDetail } from "@/api/db/manuals";
import { Button } from "@/components/ui/button";
import { useManualDeliver } from "@/hooks/use-manuals";
import { ManualLabels } from "@/lib/labels/manuals";

const TRIGGER_CLASSNAME =
	"inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-control transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 md:h-8 [&_svg]:size-4 [&_svg]:shrink-0";

export function DeliverAction({ manual, dirty }: { manual: ManualDetail; dirty: boolean }) {
	const deliver = useManualDeliver();

	const link = manual.status === "delivered" ? deliver.data?.url : undefined;

	if (manual.status === "delivered" && link === undefined) {
		return null;
	}

	return (
		<div className="flex flex-col items-end gap-2">
			{manual.status !== "delivered" && (
				<button
					type="button"
					disabled={dirty || deliver.isPending}
					onClick={() => deliver.mutate({ id: manual.id })}
					className={TRIGGER_CLASSNAME}
				>
					<Send />
					{deliver.isPending && ManualLabels.delivering}
					{!deliver.isPending && ManualLabels.deliverButton(manual.status)}
				</button>
			)}

			{link !== undefined && <DeliverLink url={link} />}
		</div>
	);
}

function DeliverLink({ url }: { url: string }) {
	const [copied, setCopied] = useState(false);
	const resetTimer = useRef<ReturnType<typeof setTimeout>>(null);
	const codeRef = useRef<HTMLElement>(null);

	const absolute = `${window.location.origin}${url}`;

	const clearResetTimer = () => {
		if (resetTimer.current !== null) {
			clearTimeout(resetTimer.current);
			resetTimer.current = null;
		}
	};

	useEffect(() => clearResetTimer, []);

	const selectForManualCopy = () => {
		const node = codeRef.current;
		if (node === null) {
			return;
		}

		const selection = window.getSelection();
		if (selection === null) {
			return;
		}

		const range = document.createRange();
		range.selectNodeContents(node);
		selection.removeAllRanges();
		selection.addRange(range);
		node.focus();
	};

	const onCopyFailure = () => {
		selectForManualCopy();
		toast.error("Não foi possível copiar o link.", {
			position: "bottom-left",
		});
	};

	const copy = () => {
		const clipboard = navigator.clipboard as Clipboard | undefined;
		if (clipboard === undefined) {
			onCopyFailure();
			return;
		}

		clipboard
			.writeText(absolute)
			.then(() => {
				setCopied(true);
				clearResetTimer();
				resetTimer.current = setTimeout(() => setCopied(false), 1500);
			})
			.catch(onCopyFailure);
	};

	return (
		<div className="w-full max-w-72 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">
			<p className="font-medium text-foreground">Link de entrega</p>

			<p className="mt-1 text-xs text-muted-foreground">Exibição única</p>

			<div className="mt-1.5 flex items-center gap-2">
				<code
					ref={codeRef}
					tabIndex={0}
					className="flex-1 truncate rounded-md bg-card px-2 py-1 font-mono text-xs text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					{absolute}
				</code>

				<Button size="sm" variant="outline" className="h-11 md:h-8" onClick={copy}>
					{copied && <Check className="text-info" />}
					{!copied && <ClipboardCopy />}
					{copied && "Copiado"}
					{!copied && "Copiar"}
				</Button>
			</div>
		</div>
	);
}
